import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Check, CircleDot, SkipForward, Circle, Flag, List, Users, Eye, Loader2, Target, MessageSquare, Radar, Ear } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { accentOf } from '../components/onsite/OnsiteGuides';
import { ago } from '../components/onsite/useOnsitePlan';
import { buildSteps, hasCheatSheet, DONE, BlockMeta, Briefing, CheatSheet, Closing, CheckItem, PeerChip, PeerPanel, Overview } from '../components/onsite/RunnerParts';

const STATUS = [
  { value: 'answered', label: 'Beantwortet', blockLabel: 'Erledigt', icon: Check, cls: 'bg-emerald-600 border-emerald-500 text-white', off: 'text-emerald-300 border-emerald-500/40' },
  { value: 'partial', label: 'Teilweise', blockLabel: 'Teilweise', icon: CircleDot, cls: 'bg-amber-600 border-amber-500 text-white', off: 'text-amber-300 border-amber-500/40' },
  { value: 'skipped', label: 'Übersprungen', blockLabel: 'Ausgelassen', icon: SkipForward, cls: 'bg-zinc-600 border-zinc-500 text-white', off: 'text-zinc-300 border-zinc-500/40' },
  { value: 'open', label: 'Offen', blockLabel: 'Offen', icon: Circle, cls: 'bg-zinc-800 border-zinc-500 text-white', off: 'text-muted-foreground border-border' },
];

export default function OnsiteRunnerPage() {
  const { planId, guideId } = useParams();
  const [guide, setGuide] = useState(null);
  const [live, setLive] = useState([]);
  const [idx, setIdx] = useState(0);
  const [notes, setNotes] = useState({});
  const [showOverview, setShowOverview] = useState(false);
  const [showCheat, setShowCheat] = useState(false);
  const [peerOpen, setPeerOpen] = useState('');
  const [saving, setSaving] = useState(false);
  const lastEdit = useRef(0);
  const noteTimers = useRef({});
  const touch = useRef(null);
  const structureAt = useRef('');
  const stepIdRef = useRef('');

  const steps = useMemo(() => buildSteps(guide), [guide]);
  const step = steps[idx];
  stepIdRef.current = step?.id || '';
  const progress = guide?.progress || {};
  const prog = (step && progress[step.id]) || {};
  const a = accentOf(guide);

  const reloadStructure = useCallback(() => api.guideGet(guideId).then((r) => {
    setGuide(r.data);
    const st = buildSteps(r.data);
    const i = st.findIndex((x) => x.id === stepIdRef.current);
    setIdx((old) => (i >= 0 ? i : Math.max(0, Math.min(old, st.length - 1))));
    toast.info(`Leitfaden aktualisiert${r.data.updated_by_name ? ` von ${r.data.updated_by_name.split(' ')[0]}` : ''} — Fragen und Blöcke neu geladen`);
  }).catch(() => {}), [guideId]);

  const applyLive = useCallback((guides) => {
    setLive(guides.filter((g) => g.id !== guideId));
    const mine = guides.find((g) => g.id === guideId);
    if (!mine) return;
    if (structureAt.current && mine.structure_at && mine.structure_at !== structureAt.current) {
      structureAt.current = mine.structure_at;
      reloadStructure();
      return;
    }
    if (Date.now() - lastEdit.current > 2000) setGuide((old) => (old ? { ...old, progress: mine.progress, counts: mine.counts, cursor_info: mine.cursor_info } : old));
  }, [guideId, reloadStructure]);

  useEffect(() => {
    api.guideGet(guideId).then((r) => {
      setGuide(r.data);
      structureAt.current = r.data.structure_at || '';
      const st = buildSteps(r.data);
      const cur = r.data.cursor?.question_id;
      const i = st.findIndex((x) => x.id === cur);
      const started = Object.keys(r.data.progress || {}).length > 0;
      const firstOpen = st.findIndex((x) => (x.kind === 'question' || x.kind === 'block') && !DONE.includes(r.data.progress?.[x.id]?.status));
      setIdx(i >= 0 ? i : started && firstOpen >= 0 ? firstOpen : 0);
    }).catch(() => toast.error('Leitfaden nicht gefunden'));
  }, [guideId]);

  useEffect(() => {
    const tick = () => api.guidesLive(planId).then((r) => applyLive(r.data.guides)).catch(() => {});
    tick();
    const t = setInterval(tick, 3000);
    return () => clearInterval(t);
  }, [planId, applyLive]);

  useEffect(() => { if (step) api.guideCursor(guideId, step.id).catch(() => {}); }, [step?.id, guideId]);

  const patchLocal = (id, patch) => {
    lastEdit.current = Date.now();
    setGuide((g) => ({ ...g, progress: { ...(g.progress || {}), [id]: { ...((g.progress || {})[id] || {}), ...patch } } }));
  };
  const send = async (id, body) => {
    setSaving(true);
    try { await api.guideProgress(guideId, { question_id: id, ...body }); } catch { toast.error('Nicht gespeichert — Verbindung?'); }
    setSaving(false);
  };
  const go = (d) => setIdx((i) => Math.max(0, Math.min(steps.length - 1, i + d)));
  const setStatus = (st) => {
    if (!step || !['question', 'block'].includes(step.kind)) return;
    const v = prog.status === st ? 'open' : st;
    patchLocal(step.id, { status: v }); send(step.id, { status: v });
    if (v !== 'open' && idx < steps.length - 1) setTimeout(() => go(1), 350);
  };
  const toggleFollowup = () => { const v = !prog.followup; patchLocal(step.id, { followup: v }); send(step.id, { followup: v }); };
  const toggleSub = (i) => {
    const cur = prog.followups_done || [];
    const v = cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i];
    patchLocal(step.id, { followups_done: v }); send(step.id, { followups_done: v });
  };
  const toggleDone = (id) => { const v = progress[id]?.status === 'answered' ? 'open' : 'answered'; patchLocal(id, { status: v }); send(id, { status: v }); };
  const setAmpel = (id, v) => { patchLocal(id, { ampel: v }); send(id, { ampel: v }); };
  const onNote = (id, v) => {
    setNotes((n) => ({ ...n, [id]: v })); lastEdit.current = Date.now();
    clearTimeout(noteTimers.current[id]);
    noteTimers.current[id] = setTimeout(() => { patchLocal(id, { note: v }); send(id, { note: v }); }, 600);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (['TEXTAREA', 'INPUT'].includes(document.activeElement?.tagName)) return;
      if (e.key === 'ArrowRight') go(1); if (e.key === 'ArrowLeft') go(-1);
      if (e.key === '1') setStatus('answered'); if (e.key === '2') setStatus('partial'); if (e.key === '3') setStatus('skipped');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  if (!guide) return <div className="min-h-dvh bg-background flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal-500" /></div>;
  const work = steps.filter((s) => s.kind === 'question' || s.kind === 'block');
  const done = work.filter((s) => DONE.includes(progress[s.id]?.status)).length;
  const pct = work.length ? Math.round((done / work.length) * 100) : 0;
  const peer = live.find((p) => p.id === peerOpen);
  const sec = step?.section;
  const secSteps = sec ? work.filter((s) => s.si === step.si) : [];
  const secDone = secSteps.filter((s) => DONE.includes(progress[s.id]?.status)).length;
  const noteVal = step ? (notes[step.id] ?? prog.note ?? '') : '';
  const isBlock = step?.kind === 'block';

  const statusRow = (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="runner-status">
        {STATUS.map((s) => {
          const on = (prog.status || 'open') === s.value;
          const Icon = s.icon;
          return (
            <button key={s.value} onClick={() => setStatus(s.value)} data-testid={`runner-status-${s.value}`}
              className={`h-12 rounded-sm border text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors ${on ? s.cls : `${s.off} bg-transparent hover:bg-secondary/40`}`}>
              <Icon className="w-4 h-4" /> {isBlock ? s.blockLabel : s.label}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <button onClick={toggleFollowup} data-testid="runner-followup-flag"
          className={`inline-flex items-center gap-1.5 text-xs border rounded-sm px-3 h-9 ${prog.followup ? 'border-amber-500 bg-amber-500/15 text-amber-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
          <Flag className="w-3.5 h-3.5" /> {prog.followup ? 'Follow-up markiert' : 'Follow-up nötig'}
        </button>
        {prog.by && prog.at && <span className="text-[10px] text-muted-foreground">zuletzt {prog.by} · {ago(prog.at)}</span>}
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1"><MessageSquare className="w-3 h-3" /> Notiz</p>
        <textarea value={noteVal} onChange={(e) => onNote(step.id, e.target.value)} rows={4} data-testid="runner-note" placeholder="Was wurde gesagt? Zahlen, Namen, Eindrücke …"
          className="w-full bg-secondary border border-border rounded-sm p-3 text-base sm:text-sm text-foreground focus:outline-none focus:border-foreground/40 resize-y select-text" />
      </div>
    </>
  );

  return (
    <div className="min-h-dvh bg-background text-foreground flex flex-col select-none" data-testid="onsite-runner-page"
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; if (Math.abs(dx) > 80 && !['TEXTAREA', 'INPUT'].includes(document.activeElement?.tagName)) go(dx < 0 ? 1 : -1); touch.current = null; }}>
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b border-border px-3 sm:px-5 pb-2.5 pt-[calc(env(safe-area-inset-top)+0.625rem)] flex items-center gap-2 flex-wrap">
        <Link to={`/projects/${guide.project_id}/onsite/${planId}`} data-testid="runner-exit" className="text-muted-foreground hover:text-foreground h-10 w-10 -ml-1 inline-flex items-center justify-center" title="Runner beenden"><X className="w-5 h-5" /></Link>
        <span className={`w-2.5 h-2.5 rounded-full ${a.solid}`} />
        <span className={`text-sm font-medium ${a.text}`} data-testid="runner-owner">{guide.owner_name}</span>
        <span className="text-xs text-muted-foreground truncate max-w-[30vw]">{guide.project_name} · {guide.title}</span>
        <span className="ml-auto text-[11px] font-mono text-muted-foreground" data-testid="runner-progress">{done}/{work.length} · {pct}%</span>
        {saving ? <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" /> : <span className="w-3" />}
        {hasCheatSheet(guide.briefing) && (
          <button onClick={() => { setShowCheat((v) => !v); setPeerOpen(''); }} data-testid="runner-cheatsheet-btn" className={`inline-flex items-center gap-1 text-[11px] border rounded-sm px-2 h-7 ${showCheat ? 'border-amber-500 text-amber-300 bg-amber-500/10' : 'border-border text-muted-foreground hover:text-foreground'}`}><Radar className="w-3.5 h-3.5" /> Spickzettel</button>
        )}
        <button onClick={() => setShowOverview(true)} data-testid="runner-overview-btn" className="inline-flex items-center gap-1 text-[11px] border border-border rounded-sm px-2 h-7 text-muted-foreground hover:text-foreground"><List className="w-3.5 h-3.5" /> Übersicht</button>
        <div className="w-full h-1 rounded-full bg-secondary overflow-hidden"><div className={`h-full ${a.solid} transition-[width] duration-300`} style={{ width: `${pct}%` }} /></div>
        {live.length > 0 && (
          <div className="w-full flex items-center gap-1.5 flex-wrap" data-testid="runner-peers">
            <Users className="w-3.5 h-3.5 text-muted-foreground" />
            {live.map((p) => <PeerChip key={p.id} p={p} active={peerOpen === p.id} onClick={() => { setPeerOpen(peerOpen === p.id ? '' : p.id); setShowCheat(false); }} />)}
          </div>
        )}
      </header>

      {!step ? (
        <main className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Dieser Leitfaden hat noch keine Inhalte.</main>
      ) : step.kind === 'briefing' ? (
        <Briefing guide={guide} onStart={() => go(1)} />
      ) : step.kind === 'closing' ? (
        <Closing guide={guide} progress={progress} toggleDone={toggleDone} setAmpel={setAmpel} onNote={onNote} notes={notes} steps={steps} />
      ) : (
        <main className="flex-1 flex flex-col max-w-3xl w-full mx-auto px-4 sm:px-6 py-5 sm:py-8 gap-5" data-testid={isBlock ? 'runner-block' : 'runner-question'}>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
            <span className={`font-mono border rounded px-1.5 py-0.5 ${a.border} ${a.text}`} data-testid="runner-label">{step.label}</span>
            <span className="font-medium text-foreground/80">{sec.title}</span>
            <BlockMeta section={sec} owner={guide.owner_name} />
            {!isBlock && <span>· {secDone}/{secSteps.length} im Block</span>}
            {sec.goal && <span className="w-full text-muted-foreground/80 italic">{sec.goal}</span>}
          </div>
          {isBlock ? (
            <>
              <h1 className="font-heading text-2xl sm:text-3xl leading-snug text-foreground" data-testid="runner-question-text">{sec.title}</h1>
              {sec.lead === 'peer' && <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"><Ear className="w-3.5 h-3.5" /> {sec.lead_name || 'Dein Partner'} führt — zuhören, beobachten, mitschreiben.</p>}
              {sec.task && <div className={`rounded-sm border px-3 py-2.5 ${a.border} ${a.bg}`} data-testid="runner-block-task"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Meine Aufgabe</p><p className="text-sm text-foreground/90 mt-1 whitespace-pre-wrap">{sec.task}</p></div>}
              {sec.must_learn && <div data-testid="runner-block-must"><p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Target className="w-3 h-3" /> Muss ich rausbekommen</p><p className="text-base text-foreground mt-1 whitespace-pre-wrap">{sec.must_learn}</p></div>}
              {(sec.checklist || []).length > 0 && (
                <div className="space-y-1.5" data-testid="runner-block-checklist">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Eye className="w-3 h-3" /> Checkliste</p>
                  {sec.checklist.map((c) => <CheckItem key={c.id} on={progress[c.id]?.status === 'answered'} text={c.text} onToggle={() => toggleDone(c.id)} testid={`runner-check-${c.id}`} />)}
                </div>
              )}
            </>
          ) : (
            <>
              {(sec.task || sec.must_learn) && step.qi === 0 && (
                <details className="text-xs text-muted-foreground border border-border/60 rounded-sm px-3 py-2" data-testid="runner-section-brief">
                  <summary className="cursor-pointer">Block-Briefing: Aufgabe & was ich rausbekommen muss</summary>
                  {sec.task && <p className="mt-1.5"><span className="text-foreground/70">Aufgabe:</span> {sec.task}</p>}
                  {sec.must_learn && <p className="mt-1"><span className="text-foreground/70">Rausbekommen:</span> {sec.must_learn}</p>}
                </details>
              )}
              <h1 className="font-heading text-2xl sm:text-3xl leading-snug text-foreground" data-testid="runner-question-text">{step.text}</h1>
              {step.goal && <p className="text-sm text-muted-foreground flex items-start gap-2" data-testid="runner-goal"><Target className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground/70" /> {step.goal}</p>}
              {(step.followups || []).length > 0 && (
                <div className="space-y-1.5" data-testid="runner-followups">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Nachfragen</p>
                  {step.followups.map((f, i) => <CheckItem key={i} on={(prog.followups_done || []).includes(i)} text={f} onToggle={() => toggleSub(i)} testid={`runner-followup-${i}`} />)}
                </div>
              )}
              {(step.watch || []).length > 0 && (
                <div className={`rounded-sm border px-3 py-2.5 ${a.border} ${a.bg}`} data-testid="runner-watch">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Eye className="w-3 h-3" /> Worauf achten</p>
                  <ul className="mt-1 space-y-0.5">{step.watch.map((w, i) => <li key={i} className="text-sm text-foreground/90">• {w}</li>)}</ul>
                </div>
              )}
            </>
          )}
          {statusRow}
        </main>
      )}

      <footer className="sticky bottom-0 z-30 bg-background/95 backdrop-blur-sm border-t border-border px-3 sm:px-5 pt-2.5 pb-[calc(env(safe-area-inset-bottom)+0.625rem)] flex items-center gap-2">
        <button onClick={() => go(-1)} disabled={idx === 0} data-testid="runner-prev" className="h-11 px-4 rounded-sm border border-border text-sm inline-flex items-center gap-1 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /> Zurück</button>
        <span className="flex-1 text-center text-[11px] text-muted-foreground">{idx + 1} / {steps.length} · ← → oder wischen · 1/2/3 = Status</span>
        <button onClick={() => go(1)} disabled={idx >= steps.length - 1} data-testid="runner-next" className={`h-11 px-5 rounded-sm text-sm font-medium text-white inline-flex items-center gap-1 disabled:opacity-30 ${a.solid}`}>{idx === steps.length - 2 ? 'Abschluss' : 'Weiter'} <ChevronRight className="w-4 h-4" /></button>
      </footer>

      {showOverview && <Overview guide={guide} steps={steps} idx={idx} onJump={setIdx} onClose={() => setShowOverview(false)} peers={live} />}
      {peer && <PeerPanel peer={peer} onClose={() => setPeerOpen('')} />}
      {showCheat && <CheatSheet briefing={guide.briefing || {}} onClose={() => setShowCheat(false)} />}
    </div>
  );
}
